import type {
    MutableToolStep,
    MutableWorkflowState,
} from '@toolproof-core/lib/_contract/workflow-overlay';

import {
    collectDraftToolSteps,
    collectMutableWorkflowToolSteps,
    hasAllRequiredBindingsForToolStep,
    mergeWorkflowStateEntries,
} from '@/workflow-builder/_pure/model/machineWorkflow';
import type { RuntimeState } from '@/workflow-builder/_contract/runtime';

export type ProjectionRuntimeState = {
    committedToolSteps: ReturnType<typeof collectMutableWorkflowToolSteps>;
    committedWorkflowStateEntries: MutableWorkflowState;
    activeDraftStepKey: string | null;
    activeDraftHasAllRequiredBindings: boolean;
};

function mergeProjectedToolSteps(
    committedToolSteps: readonly MutableToolStep[],
    draftToolSteps: readonly MutableToolStep[],
): MutableToolStep[] {
    const projectedToolSteps: MutableToolStep[] = [];
    const seenStepKeys = new Set<string>();

    const pushUnique = (toolStep: MutableToolStep) => {
        if (seenStepKeys.has(toolStep.stepKey)) {
            return;
        }
        seenStepKeys.add(toolStep.stepKey);
        projectedToolSteps.push(toolStep);
    };

    committedToolSteps.forEach(pushUnique);
    draftToolSteps.forEach(pushUnique);

    return projectedToolSteps;
}

export function deriveProjectionRuntimeState(runtimeState: RuntimeState): ProjectionRuntimeState {
    const committedToolSteps = collectMutableWorkflowToolSteps(runtimeState.mutableWorkflow);
    const draftToolSteps = runtimeState.draft ? collectDraftToolSteps(runtimeState.draft) : [];

    const projectedCommittedToolSteps = mergeProjectedToolSteps(
        committedToolSteps,
        draftToolSteps,
    );

    const committedWorkflowStateEntries = mergeWorkflowStateEntries(
        runtimeState.mutableWorkflow.workflowState,
        runtimeState.draft?.workflowState ?? {},
    );

    const selectedToolStep = runtimeState.selectionState.selectedToolStep;
    const activeDraftStepKey = selectedToolStep?.stepKey ?? null;

    const activeDraftHasAllRequiredBindings = selectedToolStep
        ? hasAllRequiredBindingsForToolStep({
            draftWorkflowState: committedWorkflowStateEntries,
            toolStep: selectedToolStep,
            includeDynamicRoles: false,
        })
        : false;

    return {
        committedToolSteps: projectedCommittedToolSteps,
        committedWorkflowStateEntries,
        activeDraftStepKey,
        activeDraftHasAllRequiredBindings,
    };
}